import { types as sdkTypes } from './sdkLoader';

const { UUID, LatLng, Money, BigDecimal } = sdkTypes;

/**
 * Rehydrate plain JSON SDK shapes (from `/api/discovery/search-results`) back
 * into real SDK type instances.
 *
 * Native Sharetribe SDK responses use transit format and auto-rehydrate UUIDs,
 * Money, LatLng, etc. Our discovery endpoint is plain `res.json()` so those
 * wrappers are lost — `formatMoney` then throws "Value must be a Money type"
 * on every listing render and the search page can't paint.
 *
 * Be defensive: only rehydrate when the value LOOKS like the wire form. Don't
 * touch values that already are SDK instances (in case anyone else upstream
 * already rehydrated them).
 */

const isPlainObject = v =>
  v != null && typeof v === 'object' && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;

const rehydrateUUID = id => {
  if (id == null) {
    return id;
  }
  if (id instanceof UUID) {
    return id;
  }
  if (typeof id === 'string') {
    return new UUID(id);
  }
  if (typeof id === 'object' && typeof id.uuid === 'string') {
    return new UUID(id.uuid);
  }
  return id;
};

const rehydrateMoney = value => {
  if (value == null || value instanceof Money) {
    return value;
  }
  if (
    isPlainObject(value) &&
    typeof value.amount === 'number' &&
    typeof value.currency === 'string'
  ) {
    return new Money(value.amount, value.currency);
  }
  return value;
};

const rehydrateLatLng = value => {
  if (value == null || value instanceof LatLng) {
    return value;
  }
  if (isPlainObject(value) && typeof value.lat === 'number' && typeof value.lng === 'number') {
    return new LatLng(value.lat, value.lng);
  }
  return value;
};

const rehydrateBigDecimal = value => {
  if (value == null || (BigDecimal && value instanceof BigDecimal)) {
    return value;
  }
  if (BigDecimal && isPlainObject(value) && typeof value.value === 'string') {
    return new BigDecimal(value.value);
  }
  return value;
};

const rehydrateRelationshipRef = ref => {
  if (!ref || !ref.data) {
    return ref;
  }
  const { data } = ref;
  if (Array.isArray(data)) {
    return {
      ...ref,
      data: data.map(d => (d && d.id ? { ...d, id: rehydrateUUID(d.id) } : d)),
    };
  }
  if (data.id) {
    return { ...ref, data: { ...data, id: rehydrateUUID(data.id) } };
  }
  return ref;
};

const rehydrateRelationships = relationships => {
  if (!relationships || typeof relationships !== 'object') {
    return relationships;
  }
  const out = {};
  Object.keys(relationships).forEach(key => {
    out[key] = rehydrateRelationshipRef(relationships[key]);
  });
  return out;
};

const rehydrateListingAttributes = attributes => {
  if (!attributes || typeof attributes !== 'object') {
    return attributes;
  }
  const out = { ...attributes };
  if ('price' in out) {
    out.price = rehydrateMoney(out.price);
  }
  if ('geolocation' in out) {
    out.geolocation = rehydrateLatLng(out.geolocation);
  }
  return out;
};

const rehydrateListing = listing => {
  if (!listing || typeof listing !== 'object') {
    return listing;
  }
  return {
    ...listing,
    id: rehydrateUUID(listing.id),
    attributes: rehydrateListingAttributes(listing.attributes),
    relationships: rehydrateRelationships(listing.relationships),
  };
};

const rehydrateUser = user => {
  if (!user || typeof user !== 'object') {
    return user;
  }
  return {
    ...user,
    id: rehydrateUUID(user.id),
    relationships: rehydrateRelationships(user.relationships),
  };
};

const variantUrlString = rawUrl => {
  if (rawUrl == null) {
    return null;
  }
  if (typeof rawUrl === 'string' && rawUrl.trim() !== '') {
    return rawUrl;
  }
  if (typeof rawUrl === 'object') {
    if (typeof rawUrl.url === 'string' && rawUrl.url.trim() !== '') {
      return rawUrl.url;
    }
    if (typeof rawUrl.href === 'string' && rawUrl.href.trim() !== '') {
      return rawUrl.href;
    }
  }
  return null;
};

const normalizeImageVariants = variants => {
  if (!variants || typeof variants !== 'object') {
    return {};
  }
  const out = {};
  Object.keys(variants).forEach(key => {
    const v = variants[key];
    if (!v || typeof v !== 'object') {
      return;
    }
    const url = variantUrlString(v.url);
    if (!url) {
      return;
    }
    out[key] = {
      ...v,
      url,
      ...(typeof v.width === 'number' ? { width: v.width } : {}),
      ...(typeof v.height === 'number' ? { height: v.height } : {}),
    };
  });
  return out;
};

const rehydrateImage = image => {
  if (!image || typeof image !== 'object') {
    return image;
  }
  const attrs = image.attributes || {};
  return {
    ...image,
    id: rehydrateUUID(image.id),
    attributes: {
      ...attrs,
      variants: normalizeImageVariants(attrs.variants),
    },
  };
};

const rehydrateGenericEntity = entity => {
  if (!entity || typeof entity !== 'object') {
    return entity;
  }
  return {
    ...entity,
    id: rehydrateUUID(entity.id),
    ...(entity.relationships
      ? { relationships: rehydrateRelationships(entity.relationships) }
      : {}),
  };
};

const rehydrateEntity = entity => {
  if (!entity || typeof entity !== 'object') {
    return entity;
  }
  switch (entity.type) {
    case 'listing':
      return rehydrateListing(entity);
    case 'user':
      return rehydrateUser(entity);
    case 'image':
      return rehydrateImage(entity);
    default:
      return rehydrateGenericEntity(entity);
  }
};

/**
 * Walk the items array (mixed primaries + listings) returned in the discovery
 * response and rehydrate every embedded SDK shape.
 */
const rehydrateMergedItems = items => {
  if (!Array.isArray(items)) {
    return items;
  }
  return items.map(item => {
    if (!item || typeof item !== 'object') {
      return item;
    }
    if (item.kind === 'listing' && item.listing) {
      return { ...item, listing: rehydrateListing(item.listing) };
    }
    return item;
  });
};

/**
 * Rehydrate the full `data` payload coming back from /api/discovery/search-results.
 * Returns a NEW data object — does not mutate the input.
 */
export const rehydrateDiscoveryResponse = data => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  return {
    ...data,
    listings: Array.isArray(data.listings) ? data.listings.map(rehydrateListing) : data.listings,
    included: Array.isArray(data.included) ? data.included.map(rehydrateEntity) : data.included,
    items: rehydrateMergedItems(data.items),
  };
};

// Exposed for tests.
export const __internal__ = {
  rehydrateUUID,
  rehydrateMoney,
  rehydrateLatLng,
  rehydrateBigDecimal,
  rehydrateListing,
  rehydrateEntity,
};
